import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import CommunicationInfo, { AddressRecord, ContactRecord, PersonalContacts } from "../../entities/CommunicationInfo";
import i18n from "../../i18n";
import CommunicationValueType from "../../util/config/communicationValueType";
import { apiCallBegan, ApiCallFailedPayload, ApiCallSuccessPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader, getWorkerId } from "../users/auth";
import Address from "../../entities/Address";

// RESPONSE DECLARATIONS //
interface CommunicationInfoResponse {
    Addresses?: AddressRecord[];
    Email?: ContactRecord;
    Phone?: ContactRecord;
    Type: CommunicationValueType;
}

// SLICE //
interface State {
    forRequesting: SliceCollectionBase<CommunicationInfo>;
    forPosting: SliceCollectionBase<CommunicationInfo>;
}

const initialState: State = {
    forRequesting: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null,
    },
    forPosting: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null,
    }
}

const slice = createSlice({
    name: "communicationInfo",
    initialState,
    reducers: {
        // REQUESTED COMMUNICATION VALUES
        workerCommunicationValuesRequested: (communicationInfo) => {
            communicationInfo.forRequesting.loading = true;
            communicationInfo.forRequesting.error = null;
        },
        workerCommunicationValuesReceived: (communicationInfo, action: PayloadAction<CommunicationInfoResponse[]>) => {
            communicationInfo.forRequesting.list = communicationValuesListSchema(action.payload);
            communicationInfo.forRequesting.loading = false;
        },
        workerCommunicationValuesRequestFailed: (communicationInfo, action: PayloadAction<ApiCallFailedPayload>) => {
            communicationInfo.forRequesting.loading = false;
            communicationInfo.forRequesting.error = action.payload;
        },
        changePosted: (communicationInfo) => {
            communicationInfo.forPosting.loading = true;
            communicationInfo.forPosting.error = null;
        },
        changePostSuccess: (communicationInfo, action: PayloadAction<ApiCallSuccessPayload>) => {
            communicationInfo.forPosting.loading = false;
            communicationInfo.forPosting.error = null;
        },
        changePostFailed: (communicationInfo, action: PayloadAction<ApiCallFailedPayload>) => {
            communicationInfo.forPosting.loading = false;
            communicationInfo.forPosting.error = action.payload;
        },

        // PUT WORKER PERSONAL CONTACTS CHANGES
        workerPersonalContactsChangePuted: (workerContacts) => {
            workerContacts.forPosting.loading = true;
            workerContacts.forPosting.error = null;
        },
        workerPersonalContactsChangePutSuccess: (workerContacts, action: PayloadAction<ApiCallSuccessPayload>) => {
            workerContacts.forPosting.loading = false;
            workerContacts.forPosting.error = null;
        },
        workerPersonalContactsChangePutFailed: (
            workerContacts, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerContacts.forPosting.loading = false;
            workerContacts.forPosting.error = action.payload;
        }
    }
});

export default slice.reducer;

const {
    workerCommunicationValuesRequested,
    workerCommunicationValuesReceived,
    workerCommunicationValuesRequestFailed,

    changePosted,
    changePostSuccess,
    changePostFailed,

    workerPersonalContactsChangePuted,
    workerPersonalContactsChangePutSuccess,
    workerPersonalContactsChangePutFailed
} = slice.actions;

export const getWorkerCommunicationInfo = () => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.communicationInfo.forRequesting.loading) {
        return;
    }

    const workerId = getWorkerId(getState());
    const url = "/api/workers/contacts/" + workerId;

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: workerCommunicationValuesRequested.type,
            onSuccess: workerCommunicationValuesReceived.type,
            onError: workerCommunicationValuesRequestFailed.type
        })
    );
};

export const postAddressChange =
    (addressData: Address) => (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.communicationInfo.forPosting.loading) {
            return;
        }
        if (!addressData) {
            console.error("Invalid address data");
            return;
        }
        //console.log("addressData", addressData);

        const workerId = getWorkerId(getState());
        dispatch(
            apiCallBegan({
                url: "/api/Workflow/Address",
                method: "POST",
                headers: {
                    ...authHeader(),
                    Accept: "*/*",
                    "Content-Type":
                        "application/json"
                },
                data: JSON.stringify({
                    WorkerId: workerId,
                    countryId: 0,
                    countryCode: null,
                    countryName: addressData.Country,
                    stateId: 0,
                    stateName: addressData.State,
                    countyId: 0,
                    countyName: addressData.County,
                    cityId: 0,
                    cityName: addressData.City,
                    postalCodeId: 0,
                    postalCode: addressData.ZipCode,
                    street: addressData.StreetName,
                    building: addressData.StreetNumber,
                    apartment: addressData.Appartment,
                    addressText: addressData.AddressText,
                    addressType: 1
                }),
                onStart: changePosted.type,
                onSuccess: changePostSuccess.type,
                onError: changePostFailed.type

            })
        );
    };

export const postEmailChange =
    (email: string) => (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.communicationInfo.forPosting.loading) {
            return;
        }
        if (!email) {
            console.error("Invalid email data");
            return;
        }

        const workerId = getWorkerId(getState());
        dispatch(
            apiCallBegan({
                url: "/api/Workflow/ContactInformation",
                method: "POST",
                headers: {
                    ...authHeader(),
                    Accept: "*/*",
                    "Content-Type":
                        "application/json",
                },
                data: JSON.stringify({
                    WorkerId: workerId,
                    Method: 2,
                    ValueType: 1,
                    Value: email
                }),
                onStart: changePosted.type,
                onSuccess: changePostSuccess.type,
                onError: changePostFailed.type
            })
        );
    };

export const postPhoneChange =
    (phone: string) => (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.communicationInfo.forPosting.loading) {
            return;
        }
        if (!phone) {
            console.error("Invalid phone data");
            return;
        }

        const workerId = getWorkerId(getState());
        dispatch(
            apiCallBegan({
                url: "/api/Workflow/ContactInformation",
                method: "POST",
                headers: {
                    ...authHeader(),
                    Accept: "*/*",
                    "Content-Type":
                        "application/json"
                },
                data: JSON.stringify({
                    WorkerId: workerId,
                    Method: 1,
                    ValueType: 1,
                    Value: phone
                }),
                onStart: changePosted.type,
                onSuccess: changePostSuccess.type,
                onError: changePostFailed.type
            })
        );
    };

export const changeWorkerPersonalContacts = (personalInfo: PersonalContacts) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.communicationInfo.forPosting.loading) {
        return;
    }
    if (!personalInfo) {
        console.error("Invalid personal contacts data");
        return;
    }
    const workerId = getWorkerId(getState());
    if (!workerId) {
        return;
    }
    personalInfo.WorkerId = workerId;

    const url = "/api/workers/personalContacts/" + workerId;

    return await dispatch(
        apiCallBegan({
            url,
            method: "PUT",
            headers: {
                ...authHeader(),
                "Content-Type": "application/json",
                "Accept-Language": i18n.language
            },
            data: JSON.stringify({
                ...personalInfo
            }),
            onStart: workerPersonalContactsChangePuted.type,
            onSuccess: workerPersonalContactsChangePutSuccess.type,
            onError: workerPersonalContactsChangePutFailed.type
        })
    );
}

// NORMALIZATION //
const communicationValueSchema = (payload: CommunicationInfoResponse): CommunicationInfo | null => {
    if (!payload) {
        return null;
    }

    return ({
        Addresses: payload.Addresses,
        Email: payload.Email,
        Phone: payload.Phone,
        Type: payload.Type
    })
};

const communicationValuesListSchema = (payload: CommunicationInfoResponse[]): CommunicationInfo[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload
        .filter((a): a is CommunicationInfoResponse => a !== null)
        .map((resp: CommunicationInfoResponse): CommunicationInfo | null => communicationValueSchema(resp))
        .filter((absence): absence is CommunicationInfo => absence !== null);
};